import { Skeleton, Stack } from '@mui/material';
import dayjs from 'dayjs';
import { StatusCodes } from 'http-status-codes';
import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import {
  json,
  LoaderFunctionArgs,
  useLoaderData,
  useParams,
} from 'react-router-dom';
import useSWR from 'swr';

import { isRESTAPIError } from '../../assets/utils/helpers';
import {
  transformAdditionalInformation,
  transformLinkedReports,
  transformAuditLogs,
  transformFilesFromReportInfo,
  transformInternalComments,
  transformReportActionFieldsValues,
} from '../../components/customer/report/helper';
import CustomerReportActions from '../../components/customer/report/reportActions';
import ReportDetails from '../../components/customer/report/reportDetails';
import CustomerReportDocuments from '../../components/customer/report/reportDocuments';
import { IS_VOICE_RECORDING, Status } from '../../constants/reportConstants';
import { useAuth } from '../../contexts/auth/authContext';
import useDepartmentData from '../../hooks/fetcher/useDepartmentData';
import { useToastNotificationContext } from '../../hooks/global/alert/useToastNotification';
import {
  useAppDispatch,
  useAppSelector,
} from '../../hooks/global/typedReduxHooks';
import ErrorType from '../../interfaces/ErrorType';
import { IMeetingNote } from '../../interfaces/MeetingNotes';
import {
  ReportDetailsObject,
  TenantNamesProps,
} from '../../interfaces/reportData';
import RESTAPIError from '../../interfaces/RESTAPIError';
import reportService from '../../services/reportService';
import {
  resetMessages,
  setPiiRemovalAccessFlag,
  setPiiRemovalRequestData,
  setPiiRemovalRequestId,
} from '../../slices/piiRemovalSlice';
import {
  setDescriptionDetectedLanguage,
  setInitialRedactedDescriptionPositions,
  setReport,
  setTenantUserList,
  setUserAccessRoles,
  setVoiceRecording,
} from '../../slices/reportDataSlice';
import {
  fetchAllInvestigatorNoteCategories,
  ReportType,
  setCurrentReport,
  setCurrentReportReference,
  setDeparmentsLoading,
  setDepartments,
  setInvestigatorNoteCategories,
  setMeetingTranscripts,
  setUnreadMessages,
} from '../../slices/reportSlice';
import { RootState, store } from '../../utils/store';
import NotFoundPage from '../public/notFoundPage';

import { ShowItemDetails } from '~/components/customer/report/reportDocuments/attachments/casemanagerAttachments/tab/ShowItemDetails';
import { usePartnerContext } from '~/contexts/partner/partnerContext';
import useDecryption from '~/hooks/cryptography/decryptText';
import PotentialReportLinksDialog from '~/pages/customer/reports/report-details/PotentialReportLinks';
import { isMutationSuccess } from '~/services/api/rest-api';
import { TenantService } from '~/services/api/tenant/endpoints';
import { useAuthSlice } from '~/slices/auth';

interface LoaderData {
  tenant:
    | {
        id: number;
        name: string;
        slug: string;
      }
    | undefined;
}

export async function loader(args: LoaderFunctionArgs) {
  const url = new URL(args.request.url);
  const tenant = url.searchParams.get('tenant');

  const query = store.dispatch(TenantService.endpoints.userTenant.initiate());

  const tenantData = await query;
  query.unsubscribe();

  if (tenantData.isSuccess) {
    const selectedTenant = tenantData.data?.assigned_tenants.find(
      (at) => at.name === tenant,
    );
    if (selectedTenant) {
      const updatedTenant = await store.dispatch(
        TenantService.endpoints.updateUserTenant.initiate({
          tenant_id: selectedTenant.id,
        }),
      );
      if (isMutationSuccess(updatedTenant)) {
        return json({
          tenant: {
            id: selectedTenant.id,
            name: selectedTenant.name,
            slug: selectedTenant.slug,
          },
        });
      }
    }
  }
  return json({
    tenant: undefined,
  });
}

const CustomerNewReportPage = () => {
  const { reportId } = useParams();
  const { tenant } = useLoaderData() as LoaderData;
  const authSlice = useAuthSlice();
  const { setSelectedTenant } = usePartnerContext();

  useEffect(() => {
    if (tenant) {
      localStorage.setItem('slug', tenant.slug);
      setSelectedTenant(tenant);
      authSlice.setCredentials({ slug: tenant.slug });
    }
    // Disable ESLint for the next line
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant]);

  const { decryptText } = useDecryption();
  const reportDetails = useSelector(
    (state: RootState) => state.reportData.report,
  );
  const { piiRemovalRequestId } = useSelector(
    (state: RootState) => state.piiRemoval,
  );
  const userAccessRoles = useSelector(
    (state: RootState) => state.reportData.userAccessRoles,
  );

  const { viewFile } = useAppSelector((state) => state.reportData);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<ErrorType>({
    errorType: false,
    message: '',
  });

  const { slug } = useAuth();
  const [companySlug, setCompanySlug] = useState<string | null>(slug);
  const { createToastNotification } = useToastNotificationContext();

  const intl = useIntl();

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!slug) {
      setCompanySlug(localStorage.getItem('slug'));
    }
  }, [slug]);

  const { departmentList } = useDepartmentData();

  const isDataCached = reportDetails.referenceId === reportId;

  //fetch data frm api
  const {
    data: reportAPIData,
    isLoading: reportLoading,
    error: reportError,
    mutate,
  } = useSWR(
    companySlug && reportId ? `tenant/${companySlug}/report/${reportId}` : '',
    {
      revalidateOnFocus: false,
    },
  );

  const { data: userAccessData, error: accessError } = useSWR('user/flags', {
    revalidateOnFocus: false,
  });

  const { data: notesData } = useSWR(
    reportId ? `schedule/notes/${reportId}` : '',
    {
      revalidateOnFocus: false,
    },
  );

  const { data: piiRemovalRequest } = useSWR(
    piiRemovalRequestId
      ? `report-pii-removal-request/${piiRemovalRequestId}`
      : '',
    {
      revalidateOnFocus: false,
    },
  );

  //set data for access roles and pii removal request from api
  useEffect(() => {
    if (userAccessData) dispatch(setUserAccessRoles(userAccessData));
    else if (accessError) {
      const apiError = accessError as RESTAPIError;
      createToastNotification(apiError?.message, 'error');
    }

    dispatch(setPiiRemovalRequestData(piiRemovalRequest));
  }, [
    userAccessData,
    accessError,
    createToastNotification,
    dispatch,
    piiRemovalRequest,
  ]);

  //set report data from report api
  useEffect(() => {
    const getData = async () => {
      setIsLoading(true);

      if (reportAPIData && notesData) {
        // Description in-transit decryption before displaying
        const descriptionResponse = reportAPIData.description;

        const descriptionText = await decryptText(
          descriptionResponse.description,
        );

        const sanitizeText = async (reportid: string) => {
          const htmlEntityPattern = /&[a-zA-Z0-9#]+;/;
          const entityFlag = htmlEntityPattern.test(descriptionText);

          if (entityFlag) {
            try {
              await reportService.getSanitizedReportDetails(
                reportid,
                companySlug ?? '',
              );
              mutate();
            } catch (returnError) {
              const apiError = returnError as RESTAPIError;
              createToastNotification(apiError.message, 'error');
            }
          }
          return null;
        };

        if (
          reportDetails.status !== Status.ARCHIVED ||
          reportDetails.status !== Status.CLOSED_NOT_ENOUGH_INFORMATION ||
          reportDetails.status !== Status.CLOSED_RESOLVED ||
          reportDetails.status !== Status.CLOSED_WHISTLEBLOWER_INITIATED
        ) {
          (async () => {
            await sanitizeText(reportAPIData.reference_id);
          })();
        }

        // TODO: this report endpoint response needs to be typed
        const {
          description_redacted_ranges: reportRedactedDescriptionRanges,
          country_verbose: country,
          form_type: formType,
          followers,
          priority,
          assignee,
          status,
          tags,
          department,
          conversation,
          risk_score: responseRiskScore,
          id: reportNo,
          reference_id: referenceId,
          report_type: reportType,
          created_at: createdAt,
          due_date: firstDueDate,
          next_review_date: nextReview,
          final_due_date: finalDueDate,
          linked_reports: linkedReports,
          audit_logs: auditLogs,
          additional_information: additionalInformation,
          report_informations: reportInformation,
          internal_comments: internalComments,
          user_tenants: allUserTenant,
          user_full_name: userFullName,
          report_custom_fields: customFields,
          is_case_locked_enabled: isCaseLockedEnabled,
          completed_date: completedAt,
          is_internally_submitted: isInternalSubmission,
          subject_individual: subjectIndividual,
          suspected_department: suspectedDepartment,
          is_whistleblower_feedback_enabled: isWhistleblowerFeedbackEnabled,
          tenant_has_unread_message: hasUnreadMessages,
          is_assignee: isAssignee,
          description_detected_language_ids: descriptionDetectedLanguage,
          transcripts,
          formatted_report_number: formattedReportNumber,
          reporter_last_login: reporterLastLogin,
          is_case_do_not_purge_enabled: isCaseDoNotPurgeEnabled,
          is_case_privileged_enabled: isCasePrivileged,
          employee_sanction: employeeSanction,
          loss_value: lossValue,
          loss_value_currency: lossValueCurrency,
          report_outcome: reportOutcome,
          report_action_fields_value: reportActionFieldValues,
          archived_fields: archivedFields,
          pending_pii_removal_request_id: pendingPiiRemovalRequestId,
          is_description_editable: isDescriptionEditable,
        } = reportAPIData;

        const transformedMeetingNotes = notesData?.map(
          (note: IMeetingNote) => ({
            id: note.id,
            content: note.description,
            name: note.author,
            createdAt: note.created_at,
            isFromMeetingNotes: true,
            category: note.category?.id ? note.category : null,
          }),
        );
        const updatedInternalComments = [
          ...(Array.isArray(internalComments)
            ? transformInternalComments(internalComments)
            : []),
          ...transformedMeetingNotes,
        ];
        updatedInternalComments.sort((a, b) =>
          dayjs(a.createdAt).diff(dayjs(b.createdAt)),
        );

        const newReportData: ReportDetailsObject = {
          formType,
          country,
          reportId: reportNo,
          referenceId,
          priority,
          firstDueDate,
          finalDueDate,
          nextReview,
          status,
          tags,
          department,
          subjectIndividual,
          suspectedDepartment,
          conversation,
          reportType,
          isCaseLockedEnabled,
          customFields,
          isInternalSubmission,
          isWhistleblowerFeedbackEnabled,
          isAssignee,
          reportInformation,
          createdAt,
          completedAt,
          description: descriptionText,
          otherDescriptionDetails: {
            id: descriptionResponse.id,
            canEditScore: descriptionResponse.can_edit_risk_score,
            riskScore: descriptionResponse.risk_score,
          },
          reporter: userFullName,
          assignee: assignee ? assignee.id : '',
          assigneeName: assignee ? assignee.full_name : '',
          isCaseDoNotPurgeEnabled: isCaseDoNotPurgeEnabled,
          isCasePrivilegedEnabled: isCasePrivileged,
          employeeSanction,
          lossValue,
          lossValueCurrency,
          reportOutcome,
          reportActionFieldValues: Array.isArray(reportActionFieldValues)
            ? transformReportActionFieldsValues(reportActionFieldValues)
            : [],
          linkedReports: Array.isArray(linkedReports)
            ? transformLinkedReports(linkedReports)
            : [],
          additionalInformation: Array.isArray(additionalInformation)
            ? transformAdditionalInformation(additionalInformation)
            : [],
          followers: followers.map((user: TenantNamesProps) => user.id),
          reportAttachments: Array.isArray(reportInformation)
            ? transformFilesFromReportInfo(reportInformation)
            : [],
          auditLogs: Array.isArray(auditLogs)
            ? transformAuditLogs(auditLogs)
            : [],
          internalComments: updatedInternalComments,
          formattedReportNumber,
          reporterLastLogin,
          archivedFields,
          pendingPiiRemovalRequestId,
          isDescriptionEditable: isDescriptionEditable,
          totalRiskScore: responseRiskScore,
        };

        if (Array.isArray(transcripts)) {
          const transformedTranscripts = transcripts
            .sort((a, b) => a.id - b.id)
            .map((transcript, index) => {
              return {
                id: index.toString(),
                title: `Transcript ${index + 1}`,
                createdAt: (transcript.created_at as string) ?? '',
                transcriptURL: (transcript.transcription as string) ?? '',
                audioSource: (transcript.audio_recording as string) ?? '',
              };
            });
          dispatch(setMeetingTranscripts(transformedTranscripts));
        }
        dispatch(
          setPiiRemovalRequestId(newReportData.pendingPiiRemovalRequestId),
        );

        dispatch(setReport(newReportData));
        dispatch(setUnreadMessages(hasUnreadMessages));
        dispatch(setTenantUserList(allUserTenant));
        dispatch(
          setInitialRedactedDescriptionPositions(
            _.isUndefined(reportRedactedDescriptionRanges)
              ? []
              : reportRedactedDescriptionRanges,
          ),
        );
        dispatch(
          setVoiceRecording(
            newReportData.reportAttachments.filter((attachment) =>
              attachment.files.some(
                (file) => file.attachmentType === IS_VOICE_RECORDING,
              ),
            ) ?? [],
          ),
        );
        dispatch(setDescriptionDetectedLanguage(descriptionDetectedLanguage));
        dispatch(resetMessages());

        // Eagerly fetch the investigator notes categories; 1 is the default page
        dispatch(setInvestigatorNoteCategories([]));
        dispatch(fetchAllInvestigatorNoteCategories(1));
        setIsLoading(false);
      } else if (reportError) {
        let errorMessage = intl.formatMessage({
          defaultMessage: 'Only admins and assigned user can view this case',
        });
        if (isRESTAPIError(reportError)) {
          if (
            `${reportError.response.status}` === String(StatusCodes.NOT_FOUND)
          ) {
            errorMessage = intl.formatMessage({
              defaultMessage: 'Report details not found on your account',
            });
          } else if (
            `${reportError.response.status}` ===
            String(StatusCodes.INTERNAL_SERVER_ERROR)
          ) {
            errorMessage = intl.formatMessage({
              defaultMessage: 'Error happened in the server',
            });
          }
        }
        setError({
          errorType: true,
          message: errorMessage,
        });
        setIsLoading(false);
      }
    };

    getData();

    return () => {
      setIsLoading(false);
    };
  }, [reportAPIData, reportError, dispatch, intl, notesData]); //eslint-disable-line

  const setDepartmentsCallback = useCallback(
    (items: Array<ReportType>) => {
      //set now the departments loading to false
      dispatch(setCurrentReportReference(reportDetails.referenceId));
      dispatch(setDeparmentsLoading(false));
      dispatch(setDepartments(items));
      dispatch(setCurrentReport(reportDetails.referenceId));
    },
    [dispatch, reportDetails],
  );

  useEffect(() => {
    if (departmentList.length > 0) {
      const storeDepartments: ReportType[] = [];
      departmentList.map(({ id, name }: { id: number; name: string }) => {
        storeDepartments.push({
          value: id,
          label: name,
        });
      });
      setDepartmentsCallback(storeDepartments);
    }
  }, [departmentList, setDepartmentsCallback]);

  const handleClick = () => {
    if (userAccessRoles?.is_read_only_report) {
      createToastNotification(
        intl.formatMessage({
          defaultMessage:
            'You are only allowed to view this report and you cannot make any changes to it.',
        }),
        'info',
      );
      return;
    } else if (
      userAccessRoles?.is_allowed_to_reopen_report == true &&
      userAccessRoles?.is_read_only_report == false &&
      reportDetails.status === Status.ARCHIVED
    ) {
      createToastNotification(
        intl.formatMessage({
          defaultMessage:
            'You need to unarchive a report first before editing.',
        }),
        'info',
      );
      return;
    } else if (
      userAccessRoles?.is_allowed_to_reopen_report == false &&
      userAccessRoles?.is_read_only_report == true &&
      (reportDetails.status === Status.CLOSED_NOT_ENOUGH_INFORMATION ||
        reportDetails.status === Status.CLOSED_RESOLVED ||
        reportDetails.status === Status.CLOSED_WHISTLEBLOWER_INITIATED)
    ) {
      createToastNotification(
        intl.formatMessage({
          defaultMessage:
            'You need to unarchive a report first before editing.',
        }),
        'info',
      );
      return;
    }
  };

  dispatch(
    setPiiRemovalAccessFlag(
      userAccessRoles?.is_allowed_to_request_pii_removal ?? false,
    ),
  );

  if (error.errorType)
    return (
      <NotFoundPage
        statusCode='404'
        statusMessage={intl.formatMessage({
          defaultMessage: 'Page not found.',
        })}
      />
    );

  return (
    <>
      <div className='flex gap-4 h-full'>
        <section className='flex-1 basis-0 flex flex-col gap-4 overflow-y-auto'>
          <Stack gap={1}>
            <Stack>
              <ReportDetails
                isLoading={(isLoading || reportLoading) && !isDataCached}
              />
            </Stack>
            <Stack onClick={handleClick} className='overflow-hidden'>
              {(isLoading || reportLoading) && !isDataCached ? (
                <Skeleton />
              ) : (
                <CustomerReportDocuments />
              )}
            </Stack>
          </Stack>
        </section>

        <section className='basis-80 h-full shrink-0 flex flex-col'>
          {(isLoading || reportLoading) && !isDataCached ? (
            <Stack gap={1} maxWidth='100%' flex={1}>
              <Skeleton />
            </Stack>
          ) : (
            <Stack
              gap={1}
              maxWidth='100%'
              flex={1}
              onClick={handleClick}
              overflow='hidden'
            >
              <CustomerReportActions mutate={mutate} />
            </Stack>
          )}
        </section>
        <PotentialReportLinksDialog />
      </div>

      {viewFile && <ShowItemDetails />}
    </>
  );
};

export default CustomerNewReportPage;
